import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../cn";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "text-sm font-medium tracking-wide select-none",
    "transition-[background,box-shadow,transform] duration-150",
    "disabled:pointer-events-none disabled:opacity-50",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)]",
    "[&_svg]:size-4 [&_svg]:shrink-0",
    "rounded-[var(--radius)]",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-[var(--color-amber)] text-[var(--color-panel-deep)]",
          "hover:bg-[var(--color-amber-bright)]",
          "active:translate-y-px",
          "shadow-[var(--shadow-amber)]",
        ],
        secondary: [
          "bg-[var(--color-panel-raised)] text-[var(--color-ink)]",
          "border border-[var(--color-panel-border)]",
          "hover:border-[var(--color-amber)] hover:text-[var(--color-amber-bright)]",
        ],
        ghost: [
          "bg-transparent text-[var(--color-ink)]",
          "hover:bg-[var(--color-panel-raised)]",
        ],
        link: [
          "bg-transparent text-[var(--color-amber)] p-0 h-auto",
          "hover:text-[var(--color-amber-bright)] hover:underline",
          "underline-offset-4",
        ],
        outline: [
          "bg-transparent text-[var(--color-ink)]",
          "border border-[var(--color-panel-border)]",
          "hover:bg-[var(--color-panel-raised)] hover:border-[var(--color-amber)]",
        ],
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        xl: "h-14 px-8 text-lg",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
